<template>
  <b-modal
    ref="modal"
    title="Edit with AI"
    size="lg"
    :no-close-on-backdrop="proposing"
    :no-close-on-esc="proposing"
    hide-header-close
    @hidden="onHidden"
  >
    <!-- INPUT phase -->
    <div v-if="!result" class="ai-edit-body">
      <p class="ai-edit-intro">
        Describe the change you want and AI will propose edits to <b>this form</b>.
        Nothing changes until you review and apply.
      </p>
      <label class="ai-field-label" for="ai-edit-instruction">What should change?</label>
      <b-form-textarea
        id="ai-edit-instruction"
        v-model="instruction"
        :disabled="proposing"
        rows="4"
        max-rows="9"
        placeholder="e.g. Remove the insurance section, add an emergency contact name and phone, and make Date of Birth required."
      />
      <div class="ai-edit-meta">
        <span :class="{ 'text-danger': instruction.length > MAX_LEN }">{{ instruction.length }}/{{ MAX_LEN }}</span>
      </div>
      <div v-if="proposing" class="ai-edit-generating">
        <b-spinner small class="mr-2" /> Working out the changes…
      </div>
    </div>

    <!-- PREVIEW phase -->
    <div v-else class="ai-edit-preview">
      <p class="ai-edit-intro">Review the proposed changes. They apply to the form only when you click <b>Apply changes</b>.</p>

      <div v-if="grouped.added.length" class="chg-group">
        <div class="chg-head added">Added</div>
        <ul><li v-for="(c, i) in grouped.added" :key="'a'+i">{{ c.text }}</li></ul>
      </div>
      <div v-if="grouped.removed.length" class="chg-group">
        <div class="chg-head removed">Removed</div>
        <ul><li v-for="(c, i) in grouped.removed" :key="'r'+i">{{ c.text }}</li></ul>
      </div>
      <div v-if="grouped.changed.length" class="chg-group">
        <div class="chg-head changed">Changed</div>
        <ul><li v-for="(c, i) in grouped.changed" :key="'c'+i">{{ c.text }}</li></ul>
      </div>
      <div v-if="grouped.warning.length" class="chg-group">
        <div class="chg-head warning">Heads up</div>
        <ul><li v-for="(c, i) in grouped.warning" :key="'w'+i" class="warn">{{ c.text }}</li></ul>
      </div>

      <p v-if="!result.changes || result.changes.length === 0" class="ai-edit-none">
        AI didn't find anything to change for that instruction. Try rephrasing.
      </p>
    </div>

    <div slot="modal-footer" class="w-100 d-flex justify-content-end">
      <template v-if="!result">
        <b-button variant="outline-secondary" class="mr-2" :disabled="proposing" @click="hide">Cancel</b-button>
        <b-button variant="primary" :disabled="!canPropose" @click="propose">
          <i class="fa fa-magic mr-1" /> Propose changes
        </b-button>
      </template>
      <template v-else>
        <b-button variant="outline-secondary" class="mr-2" @click="back">Discard</b-button>
        <b-button variant="success" :disabled="!hasChanges" @click="apply">
          <i class="fa fa-check mr-1" /> Apply changes
        </b-button>
      </template>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { extractApiError } from '@/utils/apiError';

const MIN_LEN = 3;
const MAX_LEN = 2000;

export default {
  name: 'AiEditFormModal',
  props: {
    siteId: { type: String, required: true },
    // The live builder form (ConfigData). Passed by the host; read at propose time.
    config: { type: Object, default: null },
  },
  data() {
    return { instruction: '', proposing: false, result: null, MAX_LEN };
  },
  computed: {
    canPropose() {
      const len = this.instruction.trim().length;
      return !this.proposing && len >= MIN_LEN && len <= MAX_LEN && !!this.config;
    },
    grouped() {
      const g = { added: [], removed: [], changed: [], warning: [] };
      const list = (this.result && this.result.changes) || [];
      for (const c of list) { (g[c.type] || g.changed).push(c); }
      return g;
    },
    hasChanges() {
      return !!(this.result && Array.isArray(this.result.changes) && this.result.changes.length);
    },
  },
  methods: {
    ...mapActions('forms', ['editForm']),

    show() {
      this.instruction = '';
      this.proposing = false;
      this.result = null;
      this.$refs.modal.show();
    },
    hide() { this.$refs.modal.hide(); },
    onHidden() {
      this.instruction = '';
      this.proposing = false;
      this.result = null;
    },
    back() { this.result = null; },

    async propose() {
      if (!this.canPropose) return;
      this.proposing = true;
      try {
        const response = await this.editForm({
          siteID: this.siteId,
          instruction: this.instruction.trim(),
          configData: this.config,
        });
        let body = response && response.data ? response.data : {};
        if (body && body.data && body.data.configData) body = body.data;
        if (!body || !body.configData) throw new Error('The server did not return an edited form.');
        this.result = { configData: body.configData, changes: body.changes || [], warnings: body.warnings || [], setupReport: body.setupReport || null };
      } catch (err) {
        const status = err && err.response && err.response.status;
        let msg = 'Something went wrong proposing changes. Please try again.';
        if (status === 400) msg = extractApiError(err, 'Please describe the change a bit more.');
        else if (status === 422) msg = 'AI could not turn that into edits. Try rephrasing.';
        else if (status === 502) msg = 'The AI service is busy right now. Please try again in a moment.';
        this.$toast.error(msg);
      } finally {
        this.proposing = false;
      }
    },

    apply() {
      if (!this.hasChanges) return;
      this.$emit('apply', this.result.configData, this.result.setupReport);
      const n = this.result.changes.length;
      this.$refs.modal.hide();
      this.$nextTick(() => this.$toast && this.$toast.success(`Applied ${n} change${n === 1 ? '' : 's'}.`));
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-edit-intro { color: #64748b; font-size: 0.9rem; margin-bottom: 1rem; }
.ai-field-label { font-weight: 600; font-size: 0.8125rem; color: #475569; margin-bottom: 6px; display: block; }
.ai-edit-meta { display: flex; justify-content: flex-end; font-size: 0.75rem; color: #94a3b8; margin-top: 4px; }
.ai-edit-generating { margin-top: 1rem; display: flex; align-items: center; color: #475569; font-size: 0.9rem; }
.ai-edit-none { color: #64748b; font-style: italic; }

.chg-group { margin-bottom: 14px; }
.chg-group ul { margin: 0; padding-left: 18px; }
.chg-group li { font-size: 0.9rem; color: #334155; margin-bottom: 3px; }
.chg-group li.warn { color: #b45309; }
.chg-head {
  font-weight: 700; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 5px; display: inline-block; padding: 2px 8px; border-radius: 999px;
}
.chg-head.added { background: #dcfce7; color: #15803d; }
.chg-head.removed { background: #fee2e2; color: #b91c1c; }
.chg-head.changed { background: #e0e7ff; color: #4338ca; }
.chg-head.warning { background: #fef3c7; color: #b45309; }
</style>
